import {
  CheckCircle2,
  Clock3,
  MailPlus,
  RefreshCw,
  ShieldCheck,
  UserPlus,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  SUITE_USER_ROLE_LABELS,
  SUITE_USER_ROLES,
} from "@tribunalea/shared";

const statusDetails = {
  pending: { label: "Pendiente", icon: Clock3 },
  accepted: { label: "Aceptada", icon: CheckCircle2 },
  revoked: { label: "Revocada", icon: XCircle },
  expired: { label: "Vencida", icon: XCircle },
};

function formatDate(value) {
  if (!value) return "Fecha no disponible";
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function InvitationStatus({ status }) {
  const details = statusDetails[status] || statusDetails.expired;
  const Icon = details.icon;
  return (
    <span className={`invitation-status invitation-status--${status}`}>
      <Icon size={13} aria-hidden="true" />
      {details.label}
    </span>
  );
}

export function InvitationsAdminPage({ api }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(SUITE_USER_ROLES.CITIZEN);
  const [invitations, setInvitations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadInvitations = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await api.invitations();
      setInvitations(result.invitations);
      setTotalCount(result.totalCount);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  const summary = useMemo(() => invitations.reduce((counts, invitation) => ({
    ...counts,
    [invitation.status]: (counts[invitation.status] || 0) + 1,
  }), {}), [invitations]);

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const created = await api.createInvitation({ name, email, role });
      setInvitations((current) => [
        created,
        ...current.filter((item) => item.id !== created.id),
      ]);
      setTotalCount((current) => current + 1);
      setName("");
      setEmail("");
      setSuccess(`Enviamos la invitación a ${created.email}.`);
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setSubmitting(false);
    }
  };

  const revoke = async (invitation) => {
    if (!window.confirm(
      `¿Revocar la invitación de ${invitation.name || invitation.email}? El enlace dejará de funcionar.`,
    )) return;

    setRevokingId(invitation.id);
    setError("");
    setSuccess("");
    try {
      const revoked = await api.revokeInvitation(invitation.id);
      setInvitations((current) => current.map((item) => (
        item.id === revoked.id ? revoked : item
      )));
      setSuccess(`Revocamos la invitación de ${revoked.email}.`);
    } catch (revokeError) {
      setError(revokeError.message);
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="invitations-page">
      <header className="invitations-heading">
        <div>
          <span className="support-access-eyebrow">CUENTA · ACCESO</span>
          <h1>Invitaciones personales</h1>
          <p>
            Invitá a una persona a crear su cuenta. Clerk le enviará un enlace
            único por email y podrás revocarlo mientras siga pendiente.
          </p>
        </div>
        <button
          type="button"
          className="jobs-refresh"
          disabled={loading}
          onClick={loadInvitations}
        >
          <RefreshCw size={15} aria-hidden="true" />
          {loading ? "Actualizando…" : "Actualizar"}
        </button>
      </header>

      <section className="support-access-notice">
        <ShieldCheck size={20} aria-hidden="true" />
        <span>
          <strong>Registro privado</strong>
          Cada enlace está asociado al email invitado, vence automáticamente y
          sólo puede utilizarse para crear una cuenta personal.
        </span>
      </section>

      {error ? <div className="support-access-error" role="alert">{error}</div> : null}
      {success ? <div className="invitation-success" role="status">{success}</div> : null}

      <div className="invitations-layout">
        <form className="invitation-form" onSubmit={submit}>
          <span className="invitation-form-icon" aria-hidden="true">
            <MailPlus size={22} />
          </span>
          <div>
            <h2>Nueva invitación</h2>
            <p>Ingresá los datos de la persona que va a probar Tribunalea.</p>
          </div>
          <label>
            Nombre completo
            <input
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              minLength={2}
              maxLength={120}
              autoComplete="name"
              placeholder="Ej. Ana Pérez"
              required
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              maxLength={254}
              autoComplete="email"
              placeholder="ana@estudio.com"
              required
            />
          </label>
          <label>
            Tipo de cuenta
            <select value={role} onChange={(event) => setRole(event.target.value)}>
              <option value={SUITE_USER_ROLES.CITIZEN}>Ciudadano/a · solo Civita</option>
              <option value={SUITE_USER_ROLES.LAWYER}>Abogado/a · suite profesional</option>
              <option value={SUITE_USER_ROLES.EXPERT}>Perito/a · suite profesional</option>
            </select>
          </label>
          <button type="submit" disabled={submitting}>
            <UserPlus size={17} aria-hidden="true" />
            {submitting ? "Enviando…" : "Enviar invitación"}
          </button>
        </form>

        <section className="invitation-history">
          <div className="invitation-history-heading">
            <div>
              <h2>Historial</h2>
              <p>{totalCount} invitaciones en total</p>
            </div>
            <div className="invitation-summary" aria-label="Resumen de invitaciones">
              <span><strong>{summary.pending || 0}</strong> pendientes</span>
              <span><strong>{summary.accepted || 0}</strong> aceptadas</span>
            </div>
          </div>

          <div className="invitation-list" aria-live="polite">
            {loading && !invitations.length ? (
              <div className="support-access-state">Cargando invitaciones…</div>
            ) : invitations.length ? invitations.map((invitation) => (
              <article className="invitation-row" key={invitation.id}>
                <span className="invitation-avatar" aria-hidden="true">
                  {(invitation.name || invitation.email).slice(0, 1).toUpperCase()}
                </span>
                <span className="invitation-person">
                  <strong>{invitation.name || "Sin nombre informado"}</strong>
                  <span>{invitation.email}</span>
                  <small>
                    {SUITE_USER_ROLE_LABELS[invitation.role] || "Abogado/a"}
                    {" · "}enviada el {formatDate(invitation.createdAt)}
                  </small>
                </span>
                <InvitationStatus status={invitation.status} />
                {invitation.status === "pending" ? (
                  <button
                    type="button"
                    className="invitation-revoke"
                    disabled={Boolean(revokingId)}
                    onClick={() => revoke(invitation)}
                  >
                    {revokingId === invitation.id ? "Revocando…" : "Revocar"}
                  </button>
                ) : null}
              </article>
            )) : (
              <div className="support-access-state">
                Todavía no enviaste ninguna invitación.
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
